import { NextGamesBlock } from "@/lib/interfaces/blocks";
import {Event} from "@/lib/interfaces/events";
import SectionContent from "@/components/SectionContent";
import GameList from "@/components/events/games/GameList";
import Game from "@/components/events/games/Game";
import Teams from "@/components/events/games/Teams";
import Team from "@/components/events/games/Team";
import GameDetails from "@/components/events/games/GameDetails";
import SectionTitle from "@/components/SectionTitle";
import BulbIcon from "@/components/icons/BulbIcon";
import {removeHtmlTags} from "@/lib/helpers/string";
import "@/styles/nextGames.scss";
import Link from "next/link";
import {Fragment} from "react";


type NextGamesType = {
  block: NextGamesBlock;
};

const getDaySuffix = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const formatDate = (dateString: Date) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();
  const suffix = getDaySuffix(day);

  return `${day}${suffix}-${month}-${year}`;
};

const generateArticleUrl = (event: Event) => {
  if (event.page) {
    return event.page.slug;
  }
  const { slug, end_date } = event;
  const formattedDate = formatDate(end_date);
  const fixedString = '-betting-odds-tips-predictions-preview-';
  
  return `/${slug}${fixedString}${formattedDate}`;
};
const NextGames: React.FC<NextGamesType> = ({ block }) => {
  const { events, limit } = block.content;
  const title = removeHtmlTags(block.content.title).trim();

  return (
    <section className="eo-section">
      <SectionTitle title={title}>
        <div className="icon m-icon">
          <BulbIcon />
        </div>
      </SectionTitle>
      <SectionContent>
        <GameList>
        {events && events.length > 0 ? (
          events.slice(0, limit).map((event: Event) => {
            if (!event.home_team || !event.away_team) {
              return null; // a phantom event was displaying that had no teams and didn't come up on the endpoint but had odds without this check
            }

            const articleUrl = generateArticleUrl(event);

            return (
              <Fragment key={event.id}>
                {articleUrl ? (
                  <Link href={articleUrl}>
                    <Game date={event.start_date} use="listItem">
                      <Teams>
                        <Team
                            name={event?.home_team?.name}
                            isHome={true}
                            image={event.home_team?.media ? event.home_team.media[0]?.original_webp : null}
                            odds={event.markets[0]?.selections[0]?.odds}
                        />
                        <div className="game-at">vs</div>
                        <Team
                            name={event?.away_team?.name}
                            image={event.away_team?.media ? event.away_team.media[0]?.original_webp : null}
                            odds={event.markets[0]?.selections[1]?.odds}
                        />
                      </Teams>
                      <GameDetails
                        date={event.start_date}
                        showDay={false}
                        tournamentImage={event.tournament.media ? event.tournament.media[0]?.original_webp : null}
                      />
                    </Game>
                  </Link>
                ) : (
                  <Game date={event.start_date} use="listItem">
                    <Teams>
                      <Team
                          name={event?.home_team?.name}
                          isHome={true}
                          image={event.home_team?.media ? event.home_team.media[0]?.original_webp : null}
                          odds={event.markets[0]?.selections[0]?.odds}
                      />
                      <div className="game-at">vs</div>
                      <Team
                          name={event?.away_team?.name}
                          image={event.away_team?.media ? event.away_team.media[0]?.original_webp : null}
                          odds={event.markets[0]?.selections[1]?.odds}
                      />
                    </Teams>
                    <GameDetails
                      date={event.start_date}
                      showDay={false}
                      tournamentImage={event.tournament.media ? event.tournament.media[0]?.original_webp : null}
                    />
                  </Game>
                )}
              </Fragment>
            );            
          })
        ) : (
          <div className="no-fixtures">
            Sorry, there are no fixtures coming up for this tournament.
          </div>
        )}
        </GameList>
      </SectionContent>
    </section>
  );
};

export default NextGames;